import {
	Tuple,
	Tuple0,
	Tuple2,
	Tuple3,
	Tuple4,
}	from "ts-base/tuple";
import { Fn, Fn2 }		from "ts-base/fn";
import { Nullable }		from "ts-base/nullable";
import { Numbers }		from "ts-base/numbers";
import { Predicate }	from "ts-base/predicate";
import { Equal }		from "ts-base/equal";
import { Semigroup }	from "ts-base/semigroup";
import { Comparator }	from "ts-base/comparator";
import { Arrays }		from "ts-base/arrays";
import { Maybe }		from "ts-base/maybe";
import { Either }		from "ts-base/either";
import { Validated }	from "ts-base/validated";
import { Sync }			from "ts-base/sync";
import { Async }		from "ts-base/async";
import { Zio }			from "ts-base/zio";

export type Nea<T>	= readonly [ T, ...(ReadonlyArray<T>) ];

export namespace Nea {
	//-----------------------------------------------------------------------------
	//## types

	export type Value<T>	= T extends Nea<infer X> ? X : never;

	export type Mutable<T>	= [ T, ...Array<T> ];

	//-----------------------------------------------------------------------------
	//## guards

	export const is		= <T>(items:ReadonlyArray<T>):items is Nea<T>	=> items.length !== 0;
	export const isNot	= <T>(items:ReadonlyArray<T>):items is Tuple0	=> items.length === 0;

	//-----------------------------------------------------------------------------
	//## construction

	export const single	= <T>(item:T):Nea<T>	=> [ item ];

	export const fromArray	= <T>(items:ReadonlyArray<T>):Nea<T>|null	=>
		items.length !== 0 ? items as Nea<T> : null;

	export const unsafeFromArray	= <T>(items:ReadonlyArray<T>):Nea<T>	=> {
		if (items.length === 0)	throw new Error("input array is unexpectedly empty");
		return items as Nea<T>;
	};

	export const fromNullable = <T>(item:T|null):Nea<T>|null =>
		item !== null ? [ item ] : null;

	export const toArray	= <T>(self:Nea<T>):Array<T>	=> Array.from(self);

	//-----------------------------------------------------------------------------
	//## test

	export const includes	= <T>(self:Nea<T>) => (value:T):boolean	=>
		self.includes(value);

	export const count	= <T>(self:Nea<T>) => (pred:Predicate<T>):number	=>
		Arrays.count(self)(pred);

	export const forall = <T>(self:Nea<T>) => (pred:Predicate<T>):boolean	=>
		self.every(pred);

	export const exists = <T>(self:Nea<T>) => (pred:Predicate<T>):boolean	=>
		self.some(pred);

	export const some	= exists;
	export const every	= forall;

	//-----------------------------------------------------------------------------
	//## index of

	export const firstIndexOfOrNull	= <T>(self:Nea<T>) => (search:T, start?:number):number|null	=>
		Arrays.firstIndexOfOrNull(self)(search, start);

	export const lastIndexOfOrNull	= <T>(self:Nea<T>) => (search:T, start?:number):number|null	=>
		Arrays.lastIndexOfOrNull(self)(search, start);

	export const firstIndexOf	= <T>(self:Nea<T>) => (search:T, start?:number):Maybe<number>	=>
		Arrays.firstIndexOf(self)(search, start);

	export const lastIndexOf	= <T>(self:Nea<T>) => (search:T, start?:number):Maybe<number>	=>
		Arrays.lastIndexOf(self)(search, start);

	//-----------------------------------------------------------------------------
	//## finding

	export const findFirstIndexOrNull		= <T>(self:Nea<T>, start?:number) => (pred:Fn<T, boolean>):number|null	=>
		Arrays.findFirstIndexOrNull(self, start)(pred);

	export const findLastIndexOrNull	= <T>(self:Nea<T>, start?:number) => (pred:Fn<T, boolean>):number|null	=>
		Arrays.findLastIndexOrNull(self, start)(pred);

	export const findFirstIndex		= <T>(self:Nea<T>, start?:number) => (pred:Fn<T, boolean>):Maybe<number>	=>
		Arrays.findFirstIndex(self, start)(pred);

	export const findLastIndex	= <T>(self:Nea<T>, start?:number) => (pred:Fn<T, boolean>):Maybe<number>	=>
		Arrays.findLastIndex(self, start)(pred);

	export const find	= <T>(self:Nea<T>) => (pred:Predicate<T>):Maybe<T>	=>
		Arrays.find(self)(pred);

	export const findIndex	= <T>(self:Nea<T>) => (pred:Predicate<T>):Maybe<number>	=>
		Arrays.findIndex(self)(pred);

	//-----------------------------------------------------------------------------
	//## modification

	export const append		= <T>(self:Nea<T>) => (item:T):Nea<T>	=> [ ...self, item ];
	export const prepend	= <T>(self:Nea<T>) => (item:T):Nea<T>	=> [ item, ...self ];
	export const concat		= <T>(as:Nea<T>, bs:Nea<T>):Nea<T>		=> [ ...as, ...bs ];

	export const head	= <T>(self:Nea<T>):T	=> self[0];
	export const last	= <T>(self:Nea<T>):T	=> self[self.length-1];

	export const init	= <T>(self:Nea<T>):ReadonlyArray<T>	=> self.slice(0, self.length-1);
	export const tail	= <T>(self:Nea<T>):ReadonlyArray<T>	=> self.slice(1, self.length);

	export const headAndTail	= <T>(self:Nea<T>):Tuple2<T, ReadonlyArray<T>>	=> [ head(self), tail(self) ];
	export const initAndLast	= <T>(self:Nea<T>):Tuple2<ReadonlyArray<T>, T>	=> [ init(self), last(self) ];

	export const reverse	= <T>(self:Nea<T>):Nea<T> =>
		unsafeFromArray(Arrays.reverse(self));

	export const intercalate	= <T>(self:Nea<T>) => <U>(separator:U) :Nea<T|U> =>
		unsafeFromArray(Arrays.intercalate(self)(separator));

	//-----------------------------------------------------------------------------
	//## functor

	export const map	= <S>(self:Nea<S>) => <T>(func:Fn<S, T>):Nea<T>	=>
		unsafeFromArray(self.map(func));

	export const tupleBy	= <T>(self:Nea<T>) => <U>(func:Fn<T, U>):Nea<Tuple2<T, U>>	=>
		unsafeFromArray(Arrays.tupleBy(self)(func));

	export const zipWithIndex	= <T>(self:Nea<T>):Nea<Tuple2<T, number>>	=>
		unsafeFromArray(Arrays.zipWithIndex(self));

	//-----------------------------------------------------------------------------
	//## applicative functor

	export const pure	= single;

	export const ap		= <S, T>(self:Nea<S>) => (funcs:Nea<Fn<S, T>>):Nea<T>	=>
		flatMap(funcs)(map(self));

	export const map2	= <S1, S2>(s1:Nea<S1>, s2:Nea<S2>) => <T>(func:Fn2<S1, S2, T>):Nea<T>	=>
		unsafeFromArray(Arrays.map2(s1, s2)(func));

	export const tuple2	= <T1, T2>(t1:Nea<T1>, t2:Nea<T2>):Nea<Tuple2<T1, T2>>	=>
		unsafeFromArray(Arrays.tuple2(t1, t2));

	export const tupleN	= <T1, T extends ReadonlyArray<Nea<unknown>>>(arg:Nea<T1>, ...args:T):Nea<Tuple.Prepend<T1, ValueN<T>>>	=>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		unsafeFromArray(Arrays.tupleN(arg, ...args) as any);

	export const mapN	= <S1, S extends ReadonlyArray<Nea<unknown>>>(arg:Nea<S1>, ...args:S) => <T>(func:(arg:S1, ...args:ValueN<S>)=>T):Nea<T>	=>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		unsafeFromArray(Arrays.mapN(arg, ...args)(func as any));

	type ValueN<T>	= { [P in keyof T]:Value<T[P]> };

	//-----------------------------------------------------------------------------
	//## monad

	export const flatMap	= <S>(self:Nea<S>) => <T>(func:Fn<S, Nea<T>>):Nea<T>	=>
		unsafeFromArray(self.flatMap(func));

	export const flatten	= <T>(self:Nea<Nea<T>>):Nea<T>	=>
		unsafeFromArray(self.flat());

	export const andThen	= <A, B, C>(ab:Fn<A, Nea<B>>, bc:Fn<B, Nea<C>>):Fn<A, Nea<C>>	=>
		a => flatMap(ab(a))(bc);

	export const andThen3	= <A, B, C, D>(ab:Fn<A, Nea<B>>, bc:Fn<B, Nea<C>>, cd:Fn<C, Nea<D>>):Fn<A, Nea<D>>	=>
		andThen(ab, andThen(bc, cd));

	export const andThen4	= <A, B, C, D, E>(ab:Fn<A, Nea<B>>, bc:Fn<B, Nea<C>>, cd:Fn<C, Nea<D>>, de:Fn<D, Nea<E>>):Fn<A, Nea<E>>	=>
		andThen(ab, andThen(bc, andThen(cd, de)));

	//-----------------------------------------------------------------------------
	//## zippy applicative functor

	export const zipAp	= <S>(self:Nea<S>) => <T>(func:Nea<Fn<S, T>>):Nea<T>	=>
		unsafeFromArray(Arrays.zipAp(self)(func));

	export const zipMap2	= <S1, S2>(s1:Nea<S1>, s2:Nea<S2>) => <T>(func:Fn2<S1, S2, T>):Nea<T>	=>
		unsafeFromArray(Arrays.zipMap2(s1, s2)(func));

	export const zipTuple2	= <T1, T2>(t1:Nea<T1>, t2:Nea<T2>):Nea<Tuple2<T1, T2>>	=>
		unsafeFromArray(Arrays.zip(t1, t2));

	export const zip	= zipTuple2;

	export const zipN	= <T1, T extends ReadonlyArray<Nea<unknown>>>(arg:Nea<T1>, ...args:T):Nea<Tuple.Prepend<T1, ValueN<T>>>	=>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		unsafeFromArray(Arrays.zipN(arg, ...args) as any);

	export const zipMapN	= <S1, S extends ReadonlyArray<Nea<unknown>>>(arg:Nea<S1>, ...args:S) => <T>(func:(arg:S1, ...args:ValueN<S>)=>T):Nea<T>	=>
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		unsafeFromArray(Arrays.zipMapN(arg, ...args)(func as any));

	//-----------------------------------------------------------------------------
	//## unzip

	export const unzip	= <T1, T2>(self:Nea<Tuple2<T1, T2>>):Tuple2<Nea<T1>, Nea<T2>>	=>
		Arrays.unzip(self) as unknown as Tuple2<Nea<T1>, Nea<T2>>;

	export const unzip3	= <T1, T2, T3>(self:Nea<Tuple3<T1, T2, T3>>):Tuple3<Nea<T1>, Nea<T2>, Nea<T3>>	=>
		Arrays.unzip3(self) as unknown as Tuple3<Nea<T1>, Nea<T2>, Nea<T3>>;

	export const unzip4	= <T1, T2, T3, T4>(self:Nea<Tuple4<T1, T2, T3, T4>>):Tuple4<Nea<T1>, Nea<T2>, Nea<T3>, Nea<T4>>	=>
		Arrays.unzip4(self) as unknown as Tuple4<Nea<T1>, Nea<T2>, Nea<T3>, Nea<T4>>;

	// TODO nary add unzipN

	//-----------------------------------------------------------------------------
	//## traversal

	export const traverseKleisli	= <S>(self:Nea<S>) => <C, T>(func:Fn<S, Fn<C, T>>):Fn<C, Nea<T>>	=>
		(c:C) => map(self)(it => func(it)(c));

	export const sequenceKleisli	= <C, T>(self:Nea<Fn<C, T>>):Fn<C, Nea<T>>	=>
		traverseKleisli(self)(Fn.identity);

	export const traverseNullable	= <S>(self:Nea<S>) => <T>(func:Fn<S, Nullable<T>>):Nullable<Nea<T>>	=>
		Arrays.traverseNullable(self)(func) as Nullable<Nea<T>>;

	export const sequenceNullable	= <T>(self:Nea<T|null>):Nea<T>|null	=>
		traverseNullable(self)(Fn.identity);

	export const traverseNea	= <S>(self:Nea<S>) => <T>(func:Fn<S, Nea<T>>):Nea<Nea<T>>	=>
		Arrays.traverseArray(self)(func) as unknown as Nea<Nea<T>>;

	export const sequenceNea	= <T>(self:Nea<Nea<T>>):Nea<Nea<T>>	=>
		traverseNea(self)(Fn.identity);

	export const traverseMaybe	= <S>(self:Nea<S>) => <T>(func:Fn<S, Maybe<T>>):Maybe<Nea<T>>	=>
		Arrays.traverseMaybe(self)(func) as Maybe<Nea<T>>;

	export const sequenceMaybe	= <T>(self:Nea<Maybe<T>>):Maybe<Nea<T>>	=>
		traverseMaybe(self)(Fn.identity);

	export const traverseEither	= <S>(self:Nea<S>) => <E, T>(func:Fn<S, Either<E, T>>):Either<E, Nea<T>>	=>
		Arrays.traverseEither(self)(func) as Either<E, Nea<T>>;

	export const sequenceEither	= <E, T>(self:Nea<Either<E, T>>):Either<E, Nea<T>>	=>
		traverseEither(self)(Fn.identity);

	export const traverseValidated	= <S>(self:Nea<S>) => <E, T>(func:Fn<S, Validated<E, T>>):Validated<E, Nea<T>>	=>
		Arrays.traverseValidated(self)(func) as Validated<E, Nea<T>>;

	export const sequenceValidated	= <E, T>(self:Nea<Validated<E, T>>):Validated<E, Nea<T>>	=>
		traverseValidated(self)(Fn.identity);

	export const traversePromise	= <S>(self:Nea<S>) => <T>(func:Fn<S, Promise<T>>):Promise<Nea<T>>	=>
		Arrays.traversePromise(self)(func) as unknown as Promise<Nea<T>>;

	export const sequencePromise	= <T>(self:Nea<Promise<T>>):Promise<Nea<T>>	=>
		traversePromise(self)(Fn.identity);

	export const parTraversePromise	= <S>(self:Nea<S>) => <T>(func:Fn<S, Promise<T>>):Promise<Nea<T>>	=>
		Arrays.parTraversePromise(self)(func) as unknown as Promise<Nea<T>>;

	export const parSequencePromise	= <T>(self:Nea<Promise<T>>):Promise<Nea<T>>	=>
		parTraversePromise(self)(Fn.identity);

	export const traverseSync	= <S>(self:Nea<S>) => <T>(func:Fn<S, Sync<T>>):Sync<Nea<T>>	=>
		Arrays.traverseSync(self)(func) as unknown as Sync<Nea<T>>;

	export const sequenceSync	= <S>(self:Nea<Sync<S>>):Sync<Nea<S>>	=>
		traverseSync(self)(Fn.identity);

	export const traverseAsync	= <S>(self:Nea<S>) => <T>(func:Fn<S, Async<T>>):Async<Nea<T>>	=>
		Arrays.traverseAsync(self)(func) as unknown as Async<Nea<T>>;

	export const sequenceAsync	= <S>(self:Nea<Async<S>>):Async<Nea<S>>	=>
		traverseAsync(self)(Fn.identity);

	export const parTraverseAsync	= <S>(self:Nea<S>) => <T>(func:Fn<S, Async<T>>):Async<Nea<T>>	=>
		Arrays.parTraverseAsync(self)(func) as unknown as Async<Nea<T>>;

	export const parSequenceAsync	= <S>(self:Nea<Async<S>>):Async<Nea<S>>	=>
		parTraverseAsync(self)(Fn.identity);

	export const traverseZio	= <S>(self:Nea<S>) => <C, T>(func:Fn<S, Zio<C, T>>):Zio<C, Nea<T>>	=>
		Arrays.traverseZio(self)(func) as unknown as Zio<C, Nea<T>>;

	export const sequenceZio	= <C, T>(self:Nea<Zio<C, T>>):Zio<C, Nea<T>>	=>
		traverseZio(self)(Fn.identity);

	export const parTraverseZio	= <S>(self:Nea<S>) => <C, T>(func:Fn<S, Zio<C, T>>):Zio<C, Nea<T>>	=>
		Arrays.parTraverseZio(self)(func) as unknown as Zio<C, Nea<T>>;

	export const parSequenceZio	= <C, T>(self:Nea<Zio<C, T>>):Zio<C, Nea<T>>	=>
		parTraverseZio(self)(Fn.identity);

	//-----------------------------------------------------------------------------
	//## equal

	export const equalWith	= <T>(a:Nea<T>, b:Nea<T>) => (equal:Equal<T>):boolean	=>
		Arrays.equalWith(a, b)(equal);

	export const equalBuiltin	= <T>(a:Nea<T>, b:Nea<T>):boolean	=>
		Arrays.equalBuiltin(a, b);

	/** remove duplicate items with a custom equality check */
	export const distinctWith = <T>(self:Nea<T>) => (equal:Equal<T>):Nea<T> =>
		unsafeFromArray(Arrays.distinctWith(self)(equal));

	/** remove duplicate items with builtin equality */
	export const distinctBuiltin = <T>(self:Nea<T>):Nea<T> =>
		unsafeFromArray(Arrays.distinctBuiltin(self));

	/** remove duplicate Objects in Array where sameness is determined by comparing a single property of an individual object */
	export const distinctBuiltinByField = <T>(self:Nea<T>) => <K extends keyof T>(key:K):Nea<T> =>
		unsafeFromArray(Arrays.distinctBuiltinByField(self)(key));

	/** remove duplicate items with the SameValueZero algorithm used for equality of set items */
	export const distinctBuiltinFast	= <T>(self:Nea<T>):Nea<T>	=>
		unsafeFromArray(Arrays.distinctBuiltinFast(self));

	//-----------------------------------------------------------------------------
	//## semigroup

	export const fold1	= <T>(self:Nea<T>)	=> (semigroup:Semigroup<T>):T	=>
		self.reduce(semigroup.concat);

	export const foldMap1	= <S>(self:Nea<S>) => <T>(func:Fn<S, T>) => (semigroup:Semigroup<T>):T	=>
		fold1(map(self)(func))(semigroup);

	export const foldMapIntercalated1	= <S>(self:Nea<S>) => <T>(func:Fn<S, T>) => (separator:T) => (semigroup:Semigroup<T>):T	=>
		fold1(intercalate(map(self)(func))(separator))(semigroup);

	export const concatenated	= <T>():Semigroup<Nea<T>>	=> ({
		concat:	concat,
	});

	/*
	//-----------------------------------------------------------------------------
	//## monoid

	// NOTE these do not make sense, the empty value of the monoid should _not_ be used

	export const fold	= <T>(self:Nea<T>) => (monoid:Monoid<T>):T	=>
		self.reduce(monoid.concat, monoid.empty);

	export const foldMap	= <S>(self:Nea<S>) => <T>(func:Fn<S,T>) => (monoid:Monoid<T>):T	=>
		fold(map(self)(func))(monoid);
	*/

	//-----------------------------------------------------------------------------
	//## comparator

	export const sortWith = <T>(self:Nea<T>) => (comparator:Comparator<T>):Nea<T> =>
		unsafeFromArray(Arrays.sortWith(self)(comparator));

	export const sortByWith	= <T>(self:Nea<T>) => <U>(extract:Fn<T, U>) => (comparator:Comparator<U>):Nea<T> =>
		unsafeFromArray(Arrays.sortByWith(self)(extract)(comparator));

	export const sortBuiltin	= <T>(self:Nea<T>):Nea<T>	=>
		unsafeFromArray(Arrays.sortBuiltin(self));

	export const sortBuiltinBy	= <T>(self:Nea<T>) => <U>(extract:Fn<T, U>):Nea<T>	=>
		unsafeFromArray(Arrays.sortBuiltinBy(self)(extract));

	export const sortStrings = <T extends string>(self:Nea<T>):Nea<T> =>
		unsafeFromArray(Arrays.sortStrings(self));

	export const sortNumbers = <T extends number>(self:Nea<T>):Nea<T> =>
		unsafeFromArray(Arrays.sortNumbers(self));

	export const sortBigints = <T extends bigint>(self:Nea<T>):Nea<T> =>
		unsafeFromArray(Arrays.sortBigints(self));

	export const sortBooleans = <T extends boolean>(self:Nea<T>):Nea<T> =>
		unsafeFromArray(Arrays.sortBooleans(self));

	export const sortDates = <T extends Date>(self:Nea<T>):Nea<T> =>
		unsafeFromArray(Arrays.sortDates(self));

	export const minimumWith	= <T>(self:Nea<T>) => (comparator:Comparator<T>):T	=>
		self.reduce(Comparator.minimum(comparator));

	export const maximumWith	= <T>(self:Nea<T>) => (comparator:Comparator<T>):T	=>
		self.reduce(Comparator.maximum(comparator));

	export const allMaximumWith	= <T>(self:Nea<T>)	=> (comparator:Comparator<T>):Nea<T>	=>
		unsafeFromArray(Arrays.allMaximumWith(self)(comparator));

	export const allMinimumWith	= <T>(self:ReadonlyArray<T>)	=> (comparator:Comparator<T>):Nea<T>	=>
		unsafeFromArray(Arrays.allMinimumWith(self)(comparator));

	//-----------------------------------------------------------------------------
	//## chunking

	// split into chunks of a given size
	export const chunks	= <T>(self:Nea<T>) => (size:number):Nea<Nea<T>>	=>
		Arrays.chunks(self)(size) as unknown as Nea<Nea<T>>;

	//-----------------------------------------------------------------------------
	//## sideeffects

	export const forEach	= <T>(self:Nea<T>)	=> (handler:Fn<T, void>):void	=> {
		self.forEach(handler);
	};

	//-----------------------------------------------------------------------------
	//## specials

	export const mapWithIndex	= <S>(self:Nea<S>) => <T>(func:Fn2<S, number, T>):Nea<T>	=>
		unsafeFromArray(self.map(func));

	// fisher-yates algorithm
	// attention: this is impure!
	export const randomShuffle	= <T>(self:Nea<T>):Nea<T>	=>
		unsafeFromArray(Arrays.randomShuffle(self));

	export const randomSelect	= <T>(items:ReadonlyArray<T>):T =>
		items[Numbers.randomInt(items.length)];

	//-----------------------------------------------------------------------------
	//## typeclasses

	export const equalOf	= <T>(base:Equal<T>):Equal<Nea<T>>	=>
		Arrays.equalOf(base);

	export const equalOfBuiltin	= <T>():Equal<Nea<T>>	=>
		Arrays.equalOfBuiltin();

	export const compareShorterFirst	= <T>(base:Comparator<T>):Comparator<Nea<T>>	=>
		Arrays.compareShorterFirst(base);

	export const compareLongerFirst		= <T>(base:Comparator<T>):Comparator<Nea<T>>	=>
		Arrays.compareLongerFirst(base);
}
